import type { BanDich } from '../types';
import type { viBanToken } from './ban-token.vi';

/** Bản tiếng Anh của `ban-token.vi.ts` (ADR-0100). */
export const enBanToken: Pick<BanDich, keyof typeof viBanToken> = {
  banToken: {
    chung: {
      usd: '{{so}} USD',
      xapXiVnd: '≈ {{so}} ₫',
      soTokenFox: '{{so}} Fox tokens',
    },
    goiNap: {
      timTheo: 'Search by name or code',
      timNhan: 'Search top-up packs',
      locTrangThai: 'Filter by status',
      tatCaTrangThai: 'All statuses',
      dangBan: 'On sale',
      daNgung: 'Discontinued',
      taoGoi: 'Create pack',
      suaGoi: 'Edit pack',
      batGoi: 'Put back on sale',
      ngungGoi: 'Stop selling',
      luu: 'Save pack',
      cotTen: 'Pack',
      cotToken: 'Fox tokens',
      cotThoiHan: 'Validity',
      cotGia: 'Price',
      giaRieng: 'Custom price',
      giaTheoDonGia: 'From unit price',
      chuaCoGia: 'No price yet',
      bangCaption: 'Top-up pack catalog',
      rongThat: 'No top-up packs yet',
      rongThatMo: 'Create packs for organizations to pick when requesting a top-up.',
      rongLoc: 'No packs match the filter',
      loiDanhSach: "Couldn't load top-up packs. {{loi}}",
      dangHien: '{{so}} packs',
      dangHienLoc: '{{so}} / {{tong}} packs',
      xacNhanNgung: 'Stop selling {{ten}}?',
      xacNhanNgungMo: 'Organizations can no longer pick this pack. Requests already sent for it can still be approved.',
      xacNhanBat: 'Put {{ten}} back on sale?',
      xacNhanBatMo: 'The pack appears again in the list organizations pick from when requesting a top-up.',
      daTao: 'Created pack {{ten}}.',
      daLuu: 'Saved pack {{ten}}.',
      daBat: '{{ten}} is on sale again.',
      daNgungBan: 'Stopped selling {{ten}}.',
      hopTao: 'Create top-up pack',
      hopSua: 'Edit {{ten}}',
      oTen: 'Pack name',
      oMa: 'Code',
      oMaChoTen: 'Enter a name to generate the code',
      oMaTuSinh: 'Generated from the name, fixed once saved.',
      oMaKhongDoi: "The code can't be changed.",
      oToken: 'Fox tokens',
      oThoiHan: 'Validity',
      oGia: 'Custom price (optional)',
      oGiaGoiY: 'Leave empty to price from the unit price: {{gia}}.',
      oGiaChuaDonGia: 'No Fox token unit price set: the pack can only be sold with a custom price.',
      loiTen: 'Enter a pack name.',
      loiTenDai: 'Pack name is at most 120 characters.',
      loiToken: 'Fox tokens must be a whole number from 1 to 1,000,000,000,000.',
      loiThoiHan: 'Choose 1, 3, 6, 12 months or 1 to 4 weeks.',
      loiGia: 'Price is a USD amount above 0 with up to 2 decimals, e.g. 49.99.',
    },
    donGia: {
      tab: 'Fox token unit price',
      oDonGia: 'Price per 1 million Fox tokens',
      oDonGiaGoiY: 'Sets the price of packs without a custom price and the tokens bought by amount.',
      oDonGiaTrong: 'Empty: top-ups by amount are off and packs sell only with a custom price.',
      oMucNap: 'Suggested top-up amounts (USD)',
      oMucNapGoiY: 'Numbers separated by commas, up to {{so}} amounts.',
      loiDonGia: 'Unit price is a USD amount above 0 with up to 6 decimals, e.g. 2.5.',
      loiMucNap: 'Each amount is a whole number of USD from 1 to 1,000,000.',
      loiMucNapNhieu: 'At most {{so}} amounts.',
      tyGiaDangAp: 'USD→VND rate in use:',
      tyGiaGiaTri: '{{so}} ₫ ({{nguon}}, from {{ngay}})',
      chuaCoTyGia: 'none — VND prices stay empty until a rate is added on the Exchange rates tab.',
      capNhat: 'Updated:',
      luu: 'Save unit price',
      daLuu: 'Saved the Fox token unit price.',
      loiTai: "Couldn't load the Fox token unit price. {{loi}}",
    },
    tyGia: {
      cotNguon: 'Source',
      nguonThuCong: 'Manual',
      nguonVcb: 'Vietcombank',
      dongBo: 'Sync rate',
      dangDongBo: 'Fetching...',
      nguonVcbLuc: 'Source: Vietcombank · {{luc}}',
      loiDongBo: "Couldn't fetch the rate, enter it manually.",
    },
    heSo: {
      cot: 'Fox token multipliers',
      nhomTieuDe: 'Fox token multipliers',
      nhomMo: 'Fox tokens charged per real token of this model. Input includes images and audio sent; output includes thinking tokens.',
      oVao: 'Input',
      oCache: 'Cache read',
      oRa: 'Output',
      oCacheTrong: 'Same as input',
      bangHeSoVao: 'Same as the input multiplier',
      loiHeSo: 'Multiplier from 0.001 to 1000, up to 3 decimals.',
      oTenGia: 'Model name in pricing',
      oTenGiaGoiY: 'Leave empty to use the model ID.',
      goiY: 'Break-even multipliers',
      hopTieuDe: 'Break-even multipliers from cost',
      congThuc:
        'Break-even = cost of each direction (USD / 1M real tokens) ÷ {{gia}} USD / 1M Fox tokens. Margin = current multiplier ÷ break-even − 1.',
      chuaDonGia: "No Fox token unit price set under Plans & billing → Model cost prices, so break-even can't be calculated yet.",
      bangCaption: 'Current multipliers, break-even multipliers and margin by model',
      cotModel: 'Model',
      cotDat: 'Set',
      cotHoaVon: 'Break-even',
      cotBien: 'Margin',
      giaVonLa: 'Cost {{gia}} USD / 1M real tokens',
      hoaVonLa: 'Break-even {{heSo}}',
      suaHeSo: 'Edit',
      suaHeSoCua: 'Edit multipliers of {{ten}}',
      rong: 'The catalog has no models',
      loiTai: "Couldn't load break-even multipliers. {{loi}}",
      ttOK: 'Has cost',
      ttNoName: 'No pricing model name',
      ttNoPrice: 'No cost price',
      ttNoRate: 'No rate to USD',
      ttNoUnit: 'No unit price set',
      ttUnavailable: "Couldn't read pricing",
    },
    yeuCau: {
      hopTieuDe: 'Request a token top-up',
      cheDo: 'Top-up method',
      cheDoGoi: 'Choose a pack',
      cheDoTien: 'Top up by amount',
      cheDoGiaHan: 'Extend a pack',
      oGoi: 'Top-up pack',
      chuaCoGoi: 'No packs on sale. Try topping up by amount.',
      chuaDonGia: "The platform hasn't set a Fox token unit price, so top-ups by amount are unavailable.",
      loiChonGoi: 'Choose a pack.',
      oSoTien: 'Amount',
      mucGoiY: 'Suggested amounts',
      donGiaLa: 'Unit price {{gia}} per 1 million Fox tokens.',
      xemTruocToken: '≈ {{so}} Fox tokens',
      loiSoTienTrong: 'Pick an amount or enter one.',
      loiSoTienSai: 'Amount is in USD with up to 2 decimals.',
      loiSoTienKhoang: 'Amount from 1 to 1,000,000 USD.',
      oLyDo: 'Reason (optional)',
      loiTai: "Couldn't load top-up packs. {{loi}}",
      loaiNapToken: 'Token top-up',
      ndNapToken: '{{so}} Fox tokens, {{thoiHan}}',
      duyetNapToken: 'Approve top-up',
    },
    chup: {
      goi: 'Pack {{ten}}',
      theoTien: 'By amount',
      tyGia: 'Rate {{so}} on {{ngay}}',
    },
    theoModel: {
      tieuDe: 'By model',
      caption: 'Fox input tokens, output tokens and calls by model this month',
      captionNenTang: 'Fox tokens, real tokens and multipliers by model this month',
      nhomFox: 'Fox tokens',
      nhomThat: 'Real tokens',
      nhomHeSo: 'Multipliers',
      cotModel: 'Model',
      cotVao: 'Input',
      cotCache: 'Cache read',
      cotRa: 'Output',
      cotLuot: 'Calls',
      tong: 'Total',
      khongModel: 'No model recorded',
      rong: 'No AI calls this month yet.',
      loi: "Couldn't load usage by model. {{loi}}",
    },
  },
};
